import { Button } from '@/components/ui/button'
import { Wand2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { GeneratedExample } from '@/types/emails'
import { useBusinessName } from '@/hooks/useEmailQueries'
import { TooltipInfo } from '@/components/ui/TooltipInfo'
import { eventTemplate } from '../constants'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { toast } from 'sonner'

export const Example = ({ prompt, type }: { subject?: string, prompt?: string, type: 'confirm' | 'cancel' | 'link' | 'follow_up' | 'reminder' | 'pre_call' }) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedExample, setGeneratedExample] = useState<GeneratedExample | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { data: businessName } = useBusinessName();

  const generateExample = async () => {
    if (!prompt) return toast.error('Please fill in system command');  
    
    setIsGenerating(true)

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-email-example`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          promptData: {
            business_name: businessName,
            prompt_name: businessName,
            system_prompt: prompt,
          }
        })
      })

      if (!response.ok) throw new Error('Failed to generate example')
      const result = await response.json()
      setGeneratedExample(result)
      setIsDialogOpen(true) 

    } catch (error) {
      console.error('Error generating example:', error)
      toast.error('Failed to generate example')
    } finally {
      setIsGenerating(false)
    }
  }


  const showExample = async () => {
    if (type === 'follow_up') {
      await generateExample()
    } else {
      setGeneratedExample({
        subject: content[type].subject,
        htmlBody: content[type].body,
        textBody: content[type].body,
      })
      setIsDialogOpen(true) // Open dialog after setting content
    }
  }
  return (
    <div className="space-y-4 ">
      <div className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-gray-900 text-base">Example Output</h2>
          <TooltipInfo description="You can see a preview of the email once it's sent. The content of the email is just an example with static data and can't be changed." />
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <Button
            disabled={isGenerating}
            className='text-sm bg-white text-gray-900 gap-2 flex items-center self-start px-2 py-1 rounded-md border border-gray-900 cursor-pointer  hover:opacity-60 hover:bg-white'
            size="sm"
            onClick={showExample}
          >
            {isGenerating && <Loader2 className="size-4 animate-spin" />}
            {type === 'follow_up' && !isGenerating && <Wand2 className="size-4" />}
            {type === 'follow_up' ? 'Generate' : 'Show'}
          </Button>
          <DialogContent className='p-0'>
            <div dangerouslySetInnerHTML={{ 
              __html: type === 'reminder' 
              ? reminderTemplate({ businessName: businessName || '' }) 
              : type === 'pre_call'
              ? preCallTemplate({ clientName: 'John Wick', businessName: businessName, content: 'Tell you about your experience with new technologies and ai services' })
              : eventTemplate({
                type,
                clientName: 'John Wick',
                content: generatedExample?.htmlBody || content[type].body,
                businessName: businessName || '',
                businessPhone: '123-456-7890',
                businessEmail: 'test@example.com',
                event: {
                  title: 'Mortal Duel',
                }
              }) 
            }} />
          </DialogContent>
        </Dialog>
        
      </div>
    </div>
  )
}


const content = {
  confirm: {
    subject: 'Appointment Confirmed',
    body: `
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">📅 </span> August 7, 2025</p>
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">⏰ </span> 6:30 AM</p>
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">☕ </span> Mortal Duel</p>
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">📞 </span>1 4 0 0 0 0 0 0 0 0 0</p>
    `,
  },
  cancel: {
    subject: 'Appointment Cancelled',
    body: `
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">📅 </span> August 7, 2025</p>
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">⏰ </span> 6:30 AM</p>
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">☕ </span> Mortal Duel</p>
    <p style=\"margin: 10px 0 0 0; color: #2c3e50;\"><span style=\"margin-right: 10px;\">📞 </span> 1 4 0 0 0 0 0 0 0 0 0</p>
    `,
  },
  link: {
    subject: 'Appointment Confirmation',
    body: 'https://www.google.com',
  },
  follow_up: {
    subject: 'Follow Up Info',
    body: `This is a test email以下は、最近の電話での会話に基づくフォローアップです。

お客様情報:

名前: Rick Sanchez
電話番号: 1 4 0 5 9 9 3 4 4 2 8
アポイントメント情報:

Rick Sanchezのアポイントメントは、2025年7月18日午前10時からに設定されています。このアポイントメントは、2025年7月18日午前0時にリスケジュールされました。
Jerry Smithのアポイントメント:

Jerry Smithのアポイントメントは、2025年7月18日午前7時から午前7時30分にリスケジュールされました。
Summer Smithのアポイントメント:

Summer Smithのアポイントメントは、2025年7月18日午前8時にキャンセルされました。
確認メール:

キャンセル確認メールを以下のメールアドレスに送信しました: vladyslav25cm@mail.com

他に何かお手伝いできることがあればお知らせください`,
  },
  reminder: {
    subject: 'Mortal Duel Reminder',
    body: reminderTemplate({ businessName: 'John Wick' }),
  },
  pre_call: {
    subject: 'Pre-Call Invitation',
    body: preCallTemplate({ clientName: 'John Wick', businessName: 'Your Business Name', content: 'Tell you about your experience with new technologies and ai services' }),
  },
}

export function reminderTemplate({ businessName }: {businessName: string}) {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
 
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Mortal Duel Reminder</title>
</head>
<body style="margin: 0; padding: 20px; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); padding: 40px 30px;">
      
      <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #3498db; margin: 0; font-size: 28px;">
              🔔 Mortal Duel Reminder
          </h1>
      </div>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 20px;">
          Hello <strong>John Wick</strong>!
      </p>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 30px;">
          This is a friendly reminder about your upcoming Mortal Duel.
      </p>
      
      <div style="background-color: #e8f4f8; padding: 20px; border-radius: 8px; margin-bottom: 30px; border-left: 4px solid #3498db;">
        <p style="margin: 10px 0 0 0; color: #2c3e50;"><span style="margin-right: 10px;">📅 </span> August 7, 2025</p>
        <p style="margin: 10px 0 0 0; color: #2c3e50;"><span style="margin-right: 10px;">⏰ </span> 6:30 AM</p>
        <p style="margin: 10px 0 0 0; color: #2c3e50;"><span style="margin-right: 10px;">☕ </span> Mortal Duel</p>
        <p style="margin: 10px 0 0 0; color: #2c3e50;"><span style="margin-right: 10px;">📞 </span>1 4 0 0 0 0 0 0 0 0 0</p>
      </div>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 20px;">
          We look forward to seeing you! If you need to reschedule or have any questions, please don't hesitate to contact us.
      </p>
      
      <div style="background-color: #fff3cd; padding: 15px; border-radius: 6px; margin-bottom: 20px; border-left: 4px solid #ffc107;">
          <p style="margin: 0; color: #856404; font-size: 14px;">
              <strong>Please note:</strong> We recommend arriving 10-15 minutes early for your appointment.
          </p>
      </div>
      
      <p style="font-size: 16px; color: #34495e;">
          Contact us: <strong>1 4 0 0 0 0 0 0 0 0 0</strong> or email <strong>${businessName}@gmail.com</strong>
      </p>
      
      <div style="text-align: center; margin-top: 30px;">
          <p style="font-size: 14px; color: #7f8c8d;">
              Team of ${businessName}<br>
              ${today}
          </p>
      </div>
      
  </div>
</body>
</html>`;
};

export function preCallTemplate({clientName, businessName, content}: Record<string, string>) {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
 
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Pre-Calendar Invitation</title>
</head>
<body style="margin: 0; padding: 20px; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); padding: 40px 30px;">
      
      <div style=" margin-bottom: 20px;">
          <h1 style=" margin: 0; margin-bottom: 20px; font-size: 28px;">👋</h1>
          <h2 style="font-size: 20px; font-weight: bold; color: #34495e; margin-bottom: 10px;">
            Hi ${clientName}!
          </h2>
      </div>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 10px;">
          We would like to contact you shortly to ${content}.
      </p>

      <p style="font-size: 16px; color: #34495e; margin-bottom: 10px;">
        Our contact is +14059934428 💬
    </p>
      
      
      <p style="font-size: 14px; color: #7f8c8d; margin-bottom: 20px;">
          This won't take much of your time, thank you!
      </p>
      
      <div style="text-align: center; margin-top: 50px;">
          <p style="font-size: 14px; color: #7f8c8d;">
              Team of ${businessName}<br>
              ${today}
          </p>
      </div>
      
  </div>
</body>
</html>`;
};